import React, { useState } from "react";
import { X, Loader2 } from "lucide-react";
import QuotaList from "../../../component/QuotaList";
import { createQuiz } from "../../../api/quizeApi";

const CreateQuizModal = ({
  isOpen,
  onClose,
  onQuizCreated,
  courseId,
  quotas = [],
  setQuotas,
  setRefreshFlag
}) => {
  const [formData, setFormData] = useState({
    title: "",
    timeLimit: 30,
    allowedAttempts: 1,
    status: 1
  });
  const [quotaId, setQuotaId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    const processedValue =
      type === "number"
        ? parseInt(value, 10) || ""
        : type === "checkbox"
        ? e.target.checked ? 1 : 0
        : value;

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!quotaId || !formData.title || !formData.timeLimit || !formData.allowedAttempts) {
      setError("Please fill in all required fields.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await createQuiz({
        quotaId,
        title: formData.title,
        timeLimit: formData.timeLimit,
        allowedAttempts: formData.allowedAttempts,
        status: formData.status
      });

      if (onQuizCreated) onQuizCreated(result);

      setFormData({
        title: "",
        timeLimit: 30,
        allowedAttempts: 1,
        status: 1
      });
      setQuotaId("");
      setRefreshFlag((prev) => !prev);
      onClose();
    } catch (err) {
      console.error("Failed to create quiz:", err);
      setError(err.message || "Failed to create quiz.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Create New Quiz</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4">
          {error && (
            <div className="mb-4 p-3 text-sm bg-red-50 border border-red-200 rounded-md text-red-800">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quota <span className="text-red-500">*</span>
              </label>
              <div className={!courseId ? "opacity-50 pointer-events-none" : ""}>
                <QuotaList
                  coursesId={courseId}
                  quotaId={quotaId}
                  setQuotaId={setQuotaId}
                  setQuotas={setQuotas}
                />
              </div>
            </div>

            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Quiz Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label htmlFor="timeLimit" className="block text-sm font-medium text-gray-700 mb-1">
                Time Limit (minutes) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="timeLimit"
                name="timeLimit"
                min="1"
                value={formData.timeLimit}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label htmlFor="allowedAttempts" className="block text-sm font-medium text-gray-700 mb-1">
                Allowed Attempts <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="allowedAttempts"
                name="allowedAttempts"
                min="1"
                value={formData.allowedAttempts}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="status"
                name="status"
                checked={formData.status === 1}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    status: e.target.checked ? 1 : 0
                  }))
                }
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="status" className="ml-2 block text-sm text-gray-700">
                Active Quiz
              </label>
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center disabled:bg-blue-300"
              disabled={loading}
            >
              {loading && <Loader2 className="animate-spin w-4 h-4 mr-2" />}
              Create Quiz
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateQuizModal;
